import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Megaphone, BookOpen, ClipboardList, CalendarClock, Upload, Link2, ImageIcon, ArrowRight, BadgeCheck, FileText } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TRACK = {
  none: { label: "Terbuka", cls: "bg-showty-cream text-showty-navy" },
  joined: { label: "Diikuti", cls: "bg-blue-50 text-blue-700" },
  submitted: { label: "Bukti Diupload", cls: "bg-amber-50 text-amber-700" },
  manager_approved: { label: "Approved Manager", cls: "bg-blue-100 text-blue-700" },
  approved: { label: "Selesai / Approved", cls: "bg-green-100 text-green-700" },
  rejected: { label: "Ditolak", cls: "bg-red-100 text-red-700" },
};

const STEPS = ["joined", "submitted", "approved"];
const PLATFORM_LABEL = { tiktok: "TikTok", shopee: "Shopee", both: "TikTok + Shopee" };

const DEMO_CAMPAIGNS = [
  { id: "d1", category: "Skincare", platform: "tiktok", title: "Showty Glow Serum Launch", product_image: "/images/brand-products.webp", product_knowledge: "Niacinamide 5% + Centella. Mencerahkan & menutrisi kulit kusam dalam 2 minggu.", sow: "1 video TikTok + 1 IG Reels, hook 3 detik, tag @showtynion", form_link: "https://forms.gle/example", form_label: "Isi Form Data Diri", target_role: "both", deadline: "31 Jul 2026" },
  { id: "d2", category: "Skincare", platform: "shopee", title: "Showty Hydra Moisturizer", product_image: "/images/event-2.webp", product_knowledge: "Gel moisturizer ringan, hyaluronic acid, cocok kulit berminyak.", sow: "1 video review tekstur + before/after 7 hari", target_role: "both", deadline: "28 Jul 2026" },
  { id: "d3", category: "Makeup", platform: "both", title: "Velvet Lip Tint Series", product_image: "/images/event-1.webp", product_knowledge: "Lip tint matte tahan 12 jam, 6 shade baru untuk skin tone Indonesia.", sow: "1 GRWM konten + review jujur, sertakan swatch", target_role: "beauty_creator", deadline: "20 Jul 2026" },
];

export default function CampaignPanel({ onData, demo }) {
  const [campaigns, setCampaigns] = useState([]);
  const [parts, setParts] = useState([]);

  const load = useCallback(async () => {
    if (demo) { setCampaigns(DEMO_CAMPAIGNS); setParts([]); return; }
    const [c, p] = await Promise.all([api.get("/campaigns"), api.get("/campaign-participations")]);
    setCampaigns(c.data);
    setParts(p.data);
    onData?.(p.data);
  }, [onData, demo]);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const partOf = (cid) => parts.find((p) => p.campaign_id === cid);

  if (campaigns.length === 0) {
    return <div className="rounded-2xl border border-dashed border-border bg-white/50 py-16 text-center text-sm text-muted-foreground" data-testid="campaign-empty"><Megaphone className="w-6 h-6 mx-auto mb-2 text-showty-red/50" /> Belum ada campaign. Nantikan campaign terbaru dari Showtynion!</div>;
  }

  const groups = {};
  campaigns.forEach((c) => { const k = c.category || "Lainnya"; (groups[k] = groups[k] || []).push(c); });

  return (
    <div data-testid="campaign-wrap" className="space-y-10">
      {Object.entries(groups).map(([category, list]) => (
        <section key={category} data-testid={`cat-section-${category}`}>
          <div className="flex items-center gap-3 mb-4">
            <h2 className="font-display text-2xl sm:text-3xl text-showty-navy">{category}</h2>
            <span className="text-xs rounded-full bg-showty-pink/50 px-2.5 py-0.5 text-showty-navy font-semibold">{list.length} campaign</span>
            <div className="flex-1 showty-gold-line" />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="campaign-list">
            {list.map((c) => (
              <CampaignCard key={c.id} c={c} p={partOf(c.id)} onDone={load} demo={demo} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

const CampaignCard = ({ c, p, onDone, demo }) => {
  const trackKey = p ? p.status : "none";
  const t = TRACK[trackKey] || TRACK.none;
  const curStep = p ? (p.status === "manager_approved" ? STEPS.indexOf("submitted") : STEPS.indexOf(p.status)) : -1;
  const platform = c.platform || "tiktok";
  return (
    <motion.div whileHover={{ y: -5 }} className="rounded-3xl border border-border bg-white overflow-hidden flex flex-col shadow-sm hover:shadow-lg transition-shadow" data-testid={`campaign-${c.id}`}>
      <div className="relative h-48 bg-showty-pink/30">
        {c.product_image ? <img src={c.product_image} alt={c.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Megaphone className="w-8 h-8 text-showty-red/40" /></div>}
        <span className="absolute top-3 left-3 text-[11px] rounded-full bg-showty-red text-white px-2.5 py-1 flex items-center gap-1 font-semibold shadow"><BookOpen className="w-3 h-3" /> KOL Brief</span>
        <span className="absolute top-3 right-3 text-[10px] rounded-full bg-showty-navy text-white px-2.5 py-1 font-bold tracking-wide">{c.category || "Campaign"}</span>
        <span className="absolute bottom-3 left-3 text-[10px] rounded-full bg-white/90 text-showty-navy px-2.5 py-1 font-semibold flex items-center gap-1" data-testid={`campaign-platform-${c.id}`}><Link2 className="w-3 h-3 text-showty-red" /> {PLATFORM_LABEL[platform]}</span>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-display text-xl text-showty-navy">{c.title}</h3>
        <p className="mt-1 text-sm text-muted-foreground line-clamp-2 flex-1">{c.product_knowledge || "—"}</p>
        <div className="mt-3 rounded-xl bg-showty-cream border border-border px-3 py-2">
          <p className="text-[11px] uppercase tracking-wide text-showty-gold font-semibold flex items-center gap-1"><ClipboardList className="w-3 h-3" /> SOW</p>
          <p className="text-xs text-showty-navy mt-0.5 line-clamp-1">{c.sow || "Lihat detail brief"}</p>
        </div>
        <p className="mt-2 text-xs text-showty-red flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" /> Deadline submission: {c.deadline || "—"}</p>
        {c.form_fields?.length > 0 ? (
          <div className="mt-2"><FormFillDialog campaign={c} /></div>
        ) : c.form_link ? (
          <a href={c.form_link} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-showty-navy bg-showty-pink/40 rounded-full px-3 py-1.5 hover:bg-showty-pink/60 transition-colors" data-testid={`form-link-${c.id}`}>
            <FileText className="w-3.5 h-3.5" /> {c.form_label || "Isi Form Campaign"}
          </a>
        ) : null}
        {p && (
          <>
            <div className="mt-3 flex items-center gap-1" data-testid={`campaign-track-${c.id}`}>
              {STEPS.map((s, i) => <div key={s} className={`flex-1 h-1.5 rounded-full ${i <= curStep || p.status === "approved" ? "bg-showty-red" : "bg-showty-cream"}`} />)}
            </div>
            {p.verified_platforms?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`verified-${c.id}`}>
                {p.verified_platforms.map((vp) => (
                  <span key={vp} className="text-[10px] rounded-full bg-green-100 text-green-700 px-2 py-0.5 font-semibold flex items-center gap-1"><BadgeCheck className="w-3 h-3" /> {PLATFORM_LABEL[vp]} terverifikasi</span>
                ))}
              </div>
            )}
            <p className="mt-1 text-[11px] text-muted-foreground">{t.label}{p.review_note ? ` · ${p.review_note}` : ""}</p>
          </>
        )}
        <div className="mt-4 flex items-center justify-between gap-2">
          <CampaignDetail campaign={c} />
          {!p && <JoinDialog campaign={c} onDone={onDone} demo={demo} />}
          {p && ["joined", "rejected"].includes(p.status) && <ProofDialog part={p} onDone={onDone} />}
          {p && ["submitted", "manager_approved", "approved"].includes(p.status) && <a href={p.proof_link} target="_blank" rel="noreferrer" className="text-xs text-showty-red underline underline-offset-2" data-testid={`proof-link-${c.id}`}>Lihat video/bukti</a>}
        </div>
      </div>
    </motion.div>
  );
};

const FormFillDialog = ({ campaign }) => {
  const [open, setOpen] = useState(false);
  const [ans, setAns] = useState({});
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const fields = campaign.form_fields || [];
  useEffect(() => {
    if (!open) return;
    api.get(`/campaigns/${campaign.id}/my-form-response`).then((r) => {
      if (r.data && r.data.answers) { setAns(r.data.answers); setDone(true); }
    }).catch(() => {});
  }, [open, campaign.id]);
  const submit = async () => {
    for (const fld of fields) {
      if (fld.required && !String(ans[fld.id] || "").trim()) return toast.error(`"${fld.label}" wajib diisi`);
    }
    setSaving(true);
    try {
      await api.post(`/campaigns/${campaign.id}/form-response`, { answers: ans });
      toast.success("Form terkirim ke Manager"); setDone(true); setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengirim form"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-full" data-testid={`form-fill-${campaign.id}`}>
          <FileText className="w-3.5 h-3.5 mr-1" /> {done ? "Form Terisi ✓" : (campaign.form_label || "Isi Form")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="form-fill-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-showty-navy">{campaign.form_label || "Isi Form Campaign"}</DialogTitle>
          <DialogDescription>Lengkapi data berikut untuk campaign {campaign.title}. Jawaban dikirim ke Manager.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          {fields.map((fld) => (
            <div key={fld.id}>
              <Label>{fld.label}{fld.required && <span className="text-showty-red"> *</span>}</Label>
              {fld.type === "textarea"
                ? <Textarea rows={3} className="mt-1" value={ans[fld.id] || ""} onChange={(e) => setAns((a) => ({ ...a, [fld.id]: e.target.value }))} data-testid={`form-answer-${fld.id}`} />
                : <Input type={fld.type === "number" ? "number" : "text"} className="mt-1" value={ans[fld.id] || ""} onChange={(e) => setAns((a) => ({ ...a, [fld.id]: e.target.value }))} data-testid={`form-answer-${fld.id}`} />}
            </div>
          ))}
        </div>
        <DialogFooter><Button onClick={submit} disabled={saving} className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white w-full" data-testid="form-fill-submit">{saving ? "Mengirim…" : "Kirim Form"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CampaignDetail = ({ campaign }) => (
  <Dialog>
    <DialogTrigger asChild>
      <Button size="sm" variant="outline" className="rounded-full" data-testid={`detail-${campaign.id}`}><BookOpen className="w-3.5 h-3.5 mr-1" /> Detail</Button>
    </DialogTrigger>
    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={`campaign-detail-${campaign.id}`}>
      <DialogHeader>
        <DialogTitle className="font-display text-2xl text-showty-navy">{campaign.title}</DialogTitle>
        <DialogDescription>Platform: {PLATFORM_LABEL[campaign.platform || "tiktok"]}{campaign.deadline ? ` · Deadline: ${campaign.deadline}` : ""}</DialogDescription>
      </DialogHeader>
      {campaign.product_image && <img src={campaign.product_image} alt="" className="w-full h-44 object-cover rounded-xl" />}
      <div className="rounded-xl bg-showty-cream border border-border p-4">
        <p className="text-[11px] uppercase tracking-wide text-showty-gold font-semibold flex items-center gap-1"><BookOpen className="w-3.5 h-3.5" /> Product Knowledge</p>
        <p className="text-sm text-showty-navy mt-1 whitespace-pre-wrap">{campaign.product_knowledge || "—"}</p>
      </div>
      <div className="rounded-xl border border-showty-red/20 bg-white p-4">
        <p className="text-[11px] uppercase tracking-wide text-showty-red font-semibold flex items-center gap-1"><ClipboardList className="w-3.5 h-3.5" /> SOW (Scope of Work)</p>
        <p className="text-sm text-showty-navy mt-1 whitespace-pre-wrap">{campaign.sow || "—"}</p>
      </div>
      {campaign.form_link && (
        <a href={campaign.form_link} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 rounded-full bg-showty-navy text-white py-2.5 text-sm font-semibold hover:bg-showty-navy/90" data-testid={`detail-form-link-${campaign.id}`}>
          <FileText className="w-4 h-4" /> {campaign.form_label || "Isi Form Campaign"}
        </a>
      )}
    </DialogContent>
  </Dialog>
);

const JoinDialog = ({ campaign, onDone, demo }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const platform = campaign.platform || "tiktok";
  const need = platform === "both" ? ["tiktok", "shopee"] : [platform];
  const [tiktok, setTiktok] = useState(user?.tiktok_url || "");
  const [shopee, setShopee] = useState(user?.shopee_url || "");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (demo) { toast.success("Kamu ikut campaign ini! (demo)"); setOpen(false); return; }
    setSaving(true);
    try {
      const res = await api.post(`/campaigns/${campaign.id}/join`, {
        tiktok_url: need.includes("tiktok") ? tiktok.trim() : null,
        shopee_url: need.includes("shopee") ? shopee.trim() : null,
      });
      const vp = res.data?.verified_platforms || [];
      toast.success(vp.length ? "Kamu ikut campaign & akun terverifikasi!" : "Kamu ikut campaign ini! Lengkapi link akun kapan saja agar terverifikasi.");
      setOpen(false); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal ikut campaign"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white" data-testid={`join-${campaign.id}`}>Ikuti Campaign <ArrowRight className="w-3.5 h-3.5 ml-1" /></Button>
      </DialogTrigger>
      <DialogContent data-testid="join-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-showty-navy">Hubungkan Akun {PLATFORM_LABEL[platform]}</DialogTitle>
          <DialogDescription>Campaign ini untuk <b>{PLATFORM_LABEL[platform]}</b>. Isi link akun kamu (opsional, tapi disarankan) — link valid otomatis terverifikasi. Tanpa link pun kamu tetap bisa ikut campaign.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          {need.includes("tiktok") && (
            <div>
              <Label className="flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> Link akun TikTok</Label>
              <Input className="mt-1.5" placeholder="https://www.tiktok.com/@username" value={tiktok} onChange={(e) => setTiktok(e.target.value)} data-testid="join-tiktok-input" />
            </div>
          )}
          {need.includes("shopee") && (
            <div>
              <Label className="flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> Username Shopee</Label>
              <Input className="mt-1.5" placeholder="username / nama toko Shopee (tanpa link)" value={shopee} onChange={(e) => setShopee(e.target.value)} data-testid="join-shopee-input" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white w-full" data-testid="join-submit">
            {saving ? "Menghubungkan…" : "Hubungkan & Ikuti"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ProofDialog = ({ part, onDone }) => {
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState(part.proof_link || "");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!link.trim().toLowerCase().startsWith("http")) return toast.error("Masukkan link bukti post yang valid");
    if (!file) return toast.error("Wajib upload foto bukti post");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("proof_link", link.trim());
      fd.append("file", file);
      await api.post(`/campaign-participations/${part.id}/proof`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Bukti post terkirim, menunggu approval");
      setOpen(false); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal upload bukti"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="rounded-full bg-showty-navy hover:bg-showty-navy/90 text-white flex-1" data-testid={`upload-proof-${part.id}`}><Upload className="w-3.5 h-3.5 mr-1" /> Upload Bukti Post</Button>
      </DialogTrigger>
      <DialogContent data-testid="proof-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-showty-navy">Upload Bukti Post</DialogTitle>
          <DialogDescription>Wajib foto screenshot post + link post. Akan diverifikasi Manager lalu CEO.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div>
            <Label className="flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> URL bukti review (TikTok / Shopee)</Label>
            <Input className="mt-1.5" placeholder="https://www.tiktok.com/@.../video/... atau link Shopee" value={link} onChange={(e) => setLink(e.target.value)} data-testid="proof-link-input" />
          </div>
          <div>
            <Label className="flex items-center gap-1.5"><ImageIcon className="w-3.5 h-3.5" /> Foto bukti post (screenshot)</Label>
            <Input type="file" accept="image/*" className="mt-1.5" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="proof-file-input" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white w-full" data-testid="proof-submit">
            {saving ? "Mengirim…" : "Kirim Bukti"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
